import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { LuAlertCircle, LuCheckCircle2, LuChevronRight, LuInfo } from "react-icons/lu";
import { useState } from "react";
import EProteinScore from "../components/EProteinScore";
import KitPerDay from "../components/KitPerDay";
import NutriScore from "../components/NutriScore";
import NutritionFacts from "../components/NutritionFacts";
import ProductBenefits from "../components/ProductBenefits";
import ProductImage from "../components/ProductImage";
import { RecipeList } from "../components/RecipeCard";
import ShopHeader from "../components/ShopHeader";
import { ErrorState, NutritionSkeleton } from "../components/States";
import VitaminsMinerals from "../components/VitaminsMinerals";
import { useShopProduct } from "../hooks/useShopProduct";
import { getProductRecipes } from "../../../network/service/ShopService";
import "../shop.css";

const MISSING_NAMES = { sugar: "sugar", saturatedFat: "saturated fat", sodium: "sodium" };
const SOURCE_NAMES = {
  LEGACY_IMPORT: "EatProtein catalogue",
  MANUAL: "EatProtein nutrition team",
  LAB_TESTED: "Lab test",
  PRODUCT_LABEL: "Pack label",
  OPEN_FOOD_FACTS: "Open Food Facts",
  USDA: "USDA FoodData Central",
  ICMR_NIN: "ICMR-NIN (IFCT)",
  FATSECRET: "FatSecret",
  COMBO_CONTENTS: "Added up from the products in the pack",
};

export default function NutritionInformationScreen() {
  const navigate = useNavigate();
  const { productId } = useParams();
  const [params] = useSearchParams();
  const storeId = Number(params.get("store"));
  const { data: product, status, error, retry } = useShopProduct(storeId, Number(productId));

  return (
    <div className="ep-shop">
      <ShopHeader title="Nutrition Information" onBack={() => navigate(-1)} centered showCart={false} />
      {status === "loading" && <NutritionSkeleton />}
      {status === "error" && <ErrorState error={error} onRetry={retry} />}
      {status === "ready" && <NutritionInformation product={product} packId={Number(params.get("pack")) || null} />}
    </div>
  );
}

function NutritionInformation({ product, packId }) {
  const { nutrition } = product;
  // A kit of dishes we cannot weigh has only whole-kit values.
  const perKitOnly = Boolean(nutrition?.kit && !nutrition.per100g);
  // The pack chosen on the product screen; kits keep their own per-kit view.
  const pack = nutrition?.kit ? null : product.packs.find((p) => p.priceId === packId && p.nutrition);
  const [showPack, setShowPack] = useState(Boolean(pack));
  const packFacts = showPack && pack ? pack.nutrition : null;
  const basis = packFacts ? `per ${pack.label}` : perKitOnly ? "per kit" : "per 100g";
  const summary = (
    <div className="ep-mini-product">
      <ProductImage path={product.imagePath} alt={product.name} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <h2>
          {product.name}
          {product.localName && (
            <>
              {" "}
              <span className="ep-local" lang="te">
                ({product.localName})
              </span>
            </>
          )}
        </h2>
        <small>{packFacts ? `${pack.label} pack (approx.)` : perKitOnly ? "Whole kit (approx.)" : "Per 100g (approx.)"}</small>
      </div>
      {nutrition && <NutriScore grade={nutrition.health.nutriScore?.grade} boxed />}
    </div>
  );

  if (!nutrition) {
    return (
      <div className="ep-nutrition">
        {summary}
        <div className="ep-state">
          <LuInfo aria-hidden="true" />
          <h2>Nutrition information unavailable</h2>
          <p>Our nutrition team has not added this product's nutrition facts yet.</p>
        </div>
      </div>
    );
  }

  const { health } = nutrition;
  const rated = health.eatProteinScore !== null;
  const missing = health.missingFacts.map((f) => MISSING_NAMES[f] ?? f);

  return (
    <div className="ep-nutrition">
      {summary}

      <section className={`ep-rating ${rated ? "" : "is-unrated"}`} aria-label="Overall rating">
        <EProteinScore score={health.eatProteinScore} variant="number" size={108} stroke={9} />
        <div className="ep-rating-text">
          {rated && <LuCheckCircle2 aria-hidden="true" />}
          <div style={{ minWidth: 0 }}>
            <h2>Overall Rating</h2>
            <strong style={rated ? undefined : { color: "var(--ep-muted)" }}>{health.label}</strong>
            <p>
              {rated
                ? health.verdict.summary.replace(/^[^:]+:\s*/, "") +
                  (health.ignoredFacts?.length
                    ? ` Rated without ${health.ignoredFacts.map((f) => MISSING_NAMES[f] ?? f).join(", ")} (not on the label).`
                    : "")
                : (health.notRatedReason ??
                  `We need ${missing.join(", ")} from the nutrition label before this product can be rated.`)}
            </p>
          </div>
        </div>
      </section>

      {nutrition.quality.length > 0 && (
        <div className="ep-notice" role="note">
          <LuAlertCircle aria-hidden="true" />
          <span>
            These values are being checked by our nutrition team and may change.{" "}
            {nutrition.quality.map((q) => q.message).join(" ")}
          </span>
        </div>
      )}

      {nutrition.perDay && <KitPerDay perDay={nutrition.perDay} perKit={nutrition.perServing} />}
      {nutrition.kit && !nutrition.perDay && (
        <p className="ep-prose">
          One kit holds <b>{Math.round(nutrition.kit.total.protein)} g of protein</b> in all
          {nutrition.kit.grams ? ` (${Math.round(nutrition.kit.grams)} g of food)` : ""}.
        </p>
      )}

      <section aria-labelledby="facts-title" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <h2 id="facts-title" className="ep-section-title" style={{ justifyContent: "flex-start" }}>
          Nutrition Facts <small>({basis})</small>
        </h2>
        {pack && (
          <div className="ep-chips" role="group" aria-label="Show nutrition for">
            <button type="button" className={`ep-chip ${showPack ? "" : "is-selected"}`} aria-pressed={!showPack} onClick={() => setShowPack(false)}>
              Per 100 g
            </button>
            <button type="button" className={`ep-chip ${showPack ? "is-selected" : ""}`} aria-pressed={showPack} onClick={() => setShowPack(true)}>
              Per {pack.label} pack
            </button>
          </div>
        )}
        <div className="ep-facts">
          {/* A pack's facts carry its label values (saturated fat, sodium...) scaled too. */}
          <NutritionFacts per100g={packFacts ?? nutrition.per100g ?? nutrition.kit.total} label={packFacts ?? nutrition.label} />
          <VitaminsMinerals micronutrients={packFacts?.micronutrients ?? nutrition.micronutrients} />
        </div>
        {nutrition.label.allergens.length > 0 && (
          <p className="ep-fact-empty">
            Contains {nutrition.label.allergens.map((a) => a.toLowerCase().replace(/_/g, " ")).join(", ")}.
          </p>
        )}
      </section>

      <section className="ep-card-section" aria-labelledby="benefits-title">
        <h2 id="benefits-title" className="ep-section-title">Product Benefits</h2>
        <ProductBenefits benefits={nutrition.benefits} />
      </section>

      <Recipes productId={product.productId} preview={product.recipes} />

      <p style={{ fontSize: 12, color: "var(--ep-faint)" }}>
        Source: {SOURCE_NAMES[nutrition.source] ?? nutrition.source}
        {nutrition.reviewStatus === "APPROVED" && " · Reviewed by a nutrition expert"}
        {health.scoreVersion && ` · Score ${health.scoreVersion}`}
      </p>
    </div>
  );
}

function Recipes({ productId, preview }) {
  const [all, setAll] = useState(null);
  if (!preview.total) return null;
  return (
    <section aria-labelledby="recipes-title" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div className="ep-section-title">
        <h2 id="recipes-title" style={{ fontSize: 18, fontWeight: 600 }}>Recipe Ideas</h2>
        {!all && preview.total > preview.items.length && (
          <button type="button" className="ep-link" onClick={() => getProductRecipes(productId).then((p) => setAll(p.items)).catch(() => {})}>
            View All <LuChevronRight aria-hidden="true" />
          </button>
        )}
      </div>
      <RecipeList recipes={all ?? preview.items} grid={Boolean(all)} />
    </section>
  );
}
